import dayjs, { Dayjs } from 'dayjs';

export type ChartGranularity = 'day' | 'week' | 'month';

// Below this the line turns into noise
export const MIN_PX_PER_POINT = 20;

// Finest first
const GRANULARITIES: ChartGranularity[] = ['day', 'week', 'month'];

/** Monday of the day's week. */
const weekStart = (day: Dayjs): Dayjs =>
  day.startOf('day').subtract((day.day() + 6) % 7, 'day');

const bucketStart = (day: Dayjs, granularity: ChartGranularity): Dayjs =>
  granularity === 'week' ? weekStart(day) : day.startOf(granularity);

/** Points the chart draws for the range at this zoom. */
export const pointCount = (
  start: Dayjs,
  end: Dayjs,
  granularity: ChartGranularity
): number =>
  bucketStart(end, granularity).diff(
    bucketStart(start, granularity),
    granularity
  ) + 1;

/** Zooms whose points all get enough room. Months always fit. */
export const fittingGranularities = (
  start: Dayjs,
  end: Dayjs,
  width: number
): ChartGranularity[] =>
  GRANULARITIES.filter(
    granularity =>
      granularity === 'month' ||
      pointCount(start, end, granularity) * MIN_PX_PER_POINT <= width
  );

/** The auto zoom: the finest one that fits. */
export const pickGranularity = (
  start: Dayjs,
  end: Dayjs,
  width: number
): ChartGranularity => fittingGranularities(start, end, width)[0];

/** Per-day counts (YYYY-MM-DD) as a zero-filled day or week timeline. */
export const bucketPerDay = (
  perDay: Record<string, number>,
  granularity: 'day' | 'week',
  start: Dayjs,
  end: Dayjs
): { starts: Dayjs[]; values: number[] } => {
  const first = bucketStart(start, granularity);
  const count = pointCount(start, end, granularity);
  const starts = Array.from({ length: count }, (_, index) =>
    first.add(index, granularity)
  );
  const values: number[] = Array(count).fill(0);

  Object.entries(perDay).forEach(([key, value]) => {
    const day = dayjs(key);
    if (!day.isValid()) return;
    if (day.isBefore(start, 'day') || day.isAfter(end, 'day')) return;
    values[bucketStart(day, granularity).diff(first, granularity)] += value;
  });

  return { starts, values };
};
